import { CancelToken } from 'axios';
import React, { FC, memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { getNameOfSegment } from '~src/services/getNameOfSegment';
import AvatarBox from '~src/shared/avatarBox/AvatarBox';
import SearchInput from '~src/shared/form/inputs/SearchInput';
import { ResultText, SearchItemContainer } from '~src/shared/sidebar/styles/search.sc';
import { DebounceInputWrapper, SubNavContainer } from '~src/shared/sidebar/styles/sidebar.sc';

interface OwnProps {
  selectItem: (loc: string) => void;
  deSelectSearch: () => void;
  isSelected: boolean;
}

type Props = OwnProps & RouteComponentProps<any>;

const Search: FC<Props> = memo(function SidebarSearch(props) {
  const inputNode = useRef<HTMLInputElement | null>(null);
  const [t] = useTranslation();

  useEffect(() => {
    if (props.isSelected) {
      if (inputNode.current) {
        inputNode.current.focus();
      }
    }
  }, [props.isSelected]);

  function getPlaceholderKey() {
    let whichState = 'hover';
    if (props.isSelected) {
      whichState = 'focus';
    }
    return `x.sidebar.search.input.placeholder.${whichState}`;
  }

  function setInputRef(node: HTMLInputElement) {
    inputNode.current = node;
  }

  function handleSelectElement(element: Segment, callback: () => void) {
    if (element) {
      callback();
      props.deSelectSearch();
      props.history.push(
        `/reports/${element.type}/profile/${element.type !== SegmentType.Company ? element.id : ''}`,
      );
      props.selectItem(element.type);
    }
  }

  function handleClickOutside() {
    if (props.isSelected) {
      props.deSelectSearch();
    }
  }

  function fixSearch(event: React.MouseEvent<HTMLSpanElement>) {
    event.stopPropagation();
    props.selectItem('search');
  }

  const renderListItem = (selected: boolean, item: Segment, selectItem: (item: Segment) => void) => {
    const clickThisElement = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      selectItem(item);
    };
    return (
      <SearchItemContainer onClick={clickThisElement} key={item.id} $selected={selected}>
        <AvatarBox
          size={33}
          type={item.type}
          name={getNameOfSegment(t, item) || undefined}
          url={item.image_url}
        />
        <ResultText>{getNameOfSegment(t, item, true)}</ResultText>
      </SearchItemContainer>
    );
  };

  const placeholder = {
    key: getPlaceholderKey(),
    value: t(getPlaceholderKey()),
  };

  const fetchSearchSuggestions = (value: string, token: CancelToken): Promise<Segment[]> => {
    return fetchSearchSegments(value, token, [SegmentType.User, SegmentType.Group, SegmentType.Company]);
  }

  return (
    <SubNavContainer $fixed={props.isSelected} $full>
      <DebounceInputWrapper $focused={props.isSelected} onClick={fixSearch}>
        <SearchInput
          t={t}
          fetchSearchSuggestions={fetchSearchSuggestions}
          inputPlaceholder={placeholder}
          minLength={1}
          debounceTimeout={250}
          handleSelectElement={handleSelectElement}
          setInputRef={setInputRef}
          handleClickOutside={handleClickOutside}
          renderListItem={renderListItem}
        />
      </DebounceInputWrapper>
    </SubNavContainer>
  );
});

const SearchWithRouter = withRouter(Search);
export default SearchWithRouter;
